import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminStartupListItem } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminStartupsQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/startups/')({
  component: AdminStartupsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const columns: TableColumn<AdminStartupListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (startup) => (
      <CellLink to="/admin/startups/$id" params={{ id: startup.id }} className="font-mono text-xs">
        {startup.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'Name',
    render: (startup) => <CellPrimary>{startup.name}</CellPrimary>,
  },
  {
    header: 'Founder',
    render: (startup) =>
      startup.founder ? (
        <CellLink to="/admin/users/$id" params={{ id: startup.founder.id }}>
          {startup.founder.name || startup.founder.email}
        </CellLink>
      ) : (
        <CellText>-</CellText>
      ),
  },
  {
    header: 'Status',
    width: '110px',
    render: (startup) => (
      <Badge variant={startup.status === 'ACTIVE' ? 'success' : 'secondary'} size="sm">
        {startup.status}
      </Badge>
    ),
  },
  {
    header: 'Credits',
    width: '90px',
    render: (startup) => <span className="font-medium tabular-nums">{startup.creditBalance}</span>,
  },
  {
    header: 'Created',
    width: '130px',
    render: (startup) => <CellText>{formatFullDate(startup.createdAt)}</CellText>,
  },
]

function AdminStartupsPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const startupsQuery = useAdminStartupsQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const startups = startupsQuery.data?.data?.items ?? []
  const total = startupsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search startups..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={startups}
        isLoading={startupsQuery.isLoading}
        emptyMessage="No startups found."
        keyExtractor={(s) => s.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
